import { useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, CardMedia, LinearProgress, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';

import Modal from 'components/v2/modal/Modal';
import { InputField } from 'components/v2/input/InputField';
import { MODAL_ID, MODALS_STORE } from 'constants/ui/modal';
import { MODAL_WIDTH } from 'constants/ui/style';
import { useLocalization } from 'localization';
import { colors } from '../../../../theme/mui/colors';
import { IAddNewUserParams, UserAccess } from '../../../../api';
import { useAuth } from '../../../auth/hooks';
import { InputSelectField } from '../../../../components/v2/input/InputSelectField';
import { UserRoles } from '../../../../constants/enums/status';
import { inviteUserValidationSchema } from '../../../../components/v2/division/form/schema/usersSchema';
import Alert from '@mui/material/Alert';
import { inviteUserMutation } from '../../../../api/users/query';
import notifyHandler from '../../../../utils/notifyHandler';
import { useAbortController } from 'hooks/general/useAbortController';

const InviteUserModal = () => {
  const { t } = useLocalization();
  const { toggleModal, isModalOpen } = MODALS_STORE.userInvitation();
  const { mutateAsync: inviteUserAsync } = inviteUserMutation();
  const { signal } = useAbortController();

  const { user: authenticatedUser, userIsLoading } = useAuth();
  const [exceptionMessage, setExceptionMessage] = useState<string>('');

  const departments = useMemo(
    () =>
      authenticatedUser?.branches
        .filter(({ access }) => access === UserAccess.Admin)
        .map(({ name, id }) => ({
          value: id,
          label: name,
        })) ?? [],
    [authenticatedUser]
  );

  const roles: { value: string; label: string }[] = [
    {
      value: UserRoles.ADMIN,
      label: t('common.terms.admin'),
    },
    {
      value: UserRoles.USER,
      label: t('common.terms.user'),
    },
  ];

  const {
    control,
    handleSubmit,
    getValues,
    formState: { isSubmitting: isFormSubmitting },
  } = useForm({
    defaultValues: {
      email: '',
      departmentId: '',
      role: UserRoles.USER,
    },
    resolver: yupResolver(inviteUserValidationSchema()),
  });

  //#region Methods

  function isLoading(): boolean {
    return userIsLoading || isFormSubmitting;
  }

  async function submitAsync() {
    // Clean up error message
    setExceptionMessage('');

    const values = getValues();
    const payload: IAddNewUserParams = {
      email: values['email'],
      branches: [
        {
          branchId: values['departmentId'],
          isAdmin: values['role'] === UserRoles.ADMIN,
        },
      ],
    };

    await inviteUserAsync(
      {
        payload: payload,
        signal,
      },
      {
        onSuccess: value => {
          notifyHandler({
            type: 'success',
            content: t('common.messages.invitationSentToEmail', { email: payload.email }),
          });
          toggleModal(MODAL_ID.USER_INVITATION);
        },
        onError: () => {
          setExceptionMessage(t('common.messages.somethingHappenedWhileSendingInvitationToEmail', { ...payload }));
        },
      }
    );
  }

  //#endregion

  return (
    <Modal
      isOpen={isModalOpen(MODAL_ID.USER_INVITATION)}
      toggleModal={() => toggleModal(MODAL_ID.USER_INVITATION)}
      width={MODAL_WIDTH.sm}
      isLoading={isLoading()}
      hideFooter={true}
    >
      <form
        noValidate
        onSubmit={handleSubmit(submitAsync, () => {
          setExceptionMessage(t('common.messages.someFormFieldsAreInvalidPleaseCheck'));
        })}
      >
        {isLoading() && <LinearProgress style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1101 }} />}
        <Stack>
          <Stack gap={'1.25rem'}>
            <CardMedia
              style={{ width: '7.5rem', marginBottom: '1.25rem', marginLeft: 'auto', marginRight: 'auto' }}
              component="img"
              image="/img/inviteUser.png"
            />
            <Stack>
              <Typography variant="h4" lineHeight={'120%'} textAlign={'center'}>
                {t('modals.userInvitation.inviteUser')}
              </Typography>
              <Typography variant="body1" lineHeight={'150%'} color={colors.gray['500']} textAlign={'center'}>
                {t('modals.userInvitation.inviteUserNoteMessage')}
              </Typography>
            </Stack>

            {exceptionMessage && (
              <Alert
                severity="error"
                onClose={() => {
                  setExceptionMessage('');
                }}
              >
                {exceptionMessage}
              </Alert>
            )}
            <InputField
              control={control}
              label="modals.userInvitation.emailAddress"
              required
              name="email"
              disabled={isLoading()}
            />

            <InputSelectField
              name="departmentId"
              control={control}
              options={departments}
              label={t('common.terms.department')}
              menuHeight={600}
              loading={false}
              required={true}
              disabled={isLoading()}
            />

            <InputSelectField
              name="role"
              control={control}
              options={roles}
              label={t('modals.userInvitation.userRole')}
              menuHeight={600}
              loading={false}
              required={true}
              disabled={isLoading()}
            />
            <Button
              type="submit"
              disabled={isLoading()}
              loading={isFormSubmitting}
              fullWidth
              variant="contained"
              style={{ height: '3.5rem' }}
            >
              {t('modals.userInvitation.inviteNewUser')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Modal>
  );
};
export default InviteUserModal;
